/**
 * `Track` —— 埋点的**唯一入口**（web 侧）。
 *
 * 规范：仓库根 `docs/engineering/tracking/tracking-conventions.md`
 * - §0 通道选择铁律：业务数据只走 OpenReplay；Adjust / GA 仅在其自身有采集要求时另采
 * - §1 API 清单（与 native `analytics/Track.kt` 签名逐条对齐）
 * - §2 字段字典 / §3 命名 / §5 全量原则（不 gate、不采样、不丢弃、不主动截断）
 *
 * 所有方法 fire-and-forget：`try/catch` 吞异常、无返回值、不改控制流 —— 埋点绝不影响业务。
 *
 * 2026-08-12 从 `index.ts` 拆出来（与 native `Track.kt` 对称命名）；`index.ts` 现在只做 barrel 转发。
 */

import { emitOpenReplayEvent, getTracker } from "@/src/utils/openreplay";
import {
  container,
  envelope,
  setEnvelopeIdentity,
  isValidEventName,
  redact,
  setRuntimeScreen,
  type TrackParams,
} from "./envelope";
import {
  applyIdentity,
  clearIdentity as clearIdentityFields,
  type TrackProfile,
} from "./identity";

/**
 * `Track.log` 的级别 —— **只有值得进 OpenReplay 的三个**。
 *
 * 没有 `debug` / `trace`：那是本地临时调试，不该进采集。
 * 本地调试直接用 `console.log`（不要提交）；native 侧用 `EdLog.d`（被 `BuildFlags.HTTP_LOG` gate，线上零输出）。
 *
 * 这三个级别**全量上报、不做任何过滤**（规范 §6 不阉割）—— 正因为 API 里只暴露该上报的级别，
 * 「不过滤」才是安全的。
 */
export type TrackLevel = "info" | "warn" | "error";

/**
 * 允许写入 metadata 的 key（数据字典 §3.3）。白名单防止误把 PII 之外的脏数据写成会话维度。
 *
 * ⚠️ 白名单 ≠ 会落库。实例只有 **10 个固定槽位**，后台声明了哪 10 个才决定落库，
 * 未声明的写了**静默丢弃**（SDK 不报错）。当前已声明的 10 个见
 * `docs/engineering/tracking/openreplay-runtime.md` §2.2 ——
 * 2026-08-18 运维把 `login_channel` 的槽位让给了 `ed_device_id` + `adjust_adid`。
 * 代码这边**不删 `login_channel`**：写了只是丢弃，删掉反而要动 `IDENTITY_FIELDS`
 * 与登出清理的对称性（`clearIdentityFields` 必须清每一个身份 key）。
 *
 * 与 native `analytics/Track.kt` 的 `ALLOWED_CONTEXT_KEYS` 是同一份清单。
 */
const ALLOWED_CONTEXT_KEYS = new Set([
  "owner_id",
  "email",
  "twitter_handle",
  "phone",
  "login_channel",
  "app_run_id",
  "native_or_session",
  "web_or_session",
  "platform",
  "container",
  "env",
  "app_version",
  "build",
  "web_rev",
  "ed_device_id",
  "adjust_adid",
  "device_model",
  "os_version",
  "plan",
  "locale",
]);

function setMetadata(key: string, value: string): void {
  try {
    getTracker()?.setMetadata?.(key, value);
  } catch {
    // 埋点不影响业务
  }
}

/** 事件名非法时的兜底：降级成一条 `log`，把原始名留在 payload 里，不静默丢。 */
function emitInvalidName(name: string, params?: TrackParams): void {
  emitOpenReplayEvent(
    "log",
    envelope(
      {
        level: "warn",
        tag: "Track",
        msg: "invalid event name",
        invalid_event_name: String(name).slice(0, 128),
        ...redact(params),
      },
      "log",
    ),
  );
}

/** [Track.view] / [Track.event] 的共同实现 —— 只差 `event_type`。 */
function emit(
  name: string,
  params: TrackParams | undefined,
  eventType: "view" | "action",
): void {
  try {
    if (!isValidEventName(name)) {
      emitInvalidName(name, params);
      return;
    }
    emitOpenReplayEvent(name, envelope(redact(params), eventType));
  } catch {
    // 埋点不影响业务
  }
}

export const Track = {
  /**
   * 曝光。有 DOM 宿主时优先用 `data-or-view`（声明式，见 `domBridge.ts`），此 API 供无宿主场景。
   *
   * 与 [event] 的区别**不只是语义** —— envelope 里 `event_type` 会是 `"view"`，
   * 后台可以直接按字段筛出所有曝光，不用去 match 事件名后缀。
   */
  view(name: string, params?: TrackParams): void {
    emit(name, params, "view");
  },

  /** 业务事件（点击后的结果、异步完成、SDK 回调、程序化跳转）。`event_type = "action"`。 */
  event(name: string, params?: TrackParams): void {
    emit(name, params, "action");
  },

  /**
   * 点击打标。与 native `Track.click` **同名同签名同语义**（都是「上报一次名为 `label` 的点击」），
   * 差异只在承载消息，而那是 SDK 能力差异，不是 API 语义差异：
   *
   * | | 承载 | 说明 |
   * |---|---|---|
   * | native | `ORMobileClickEvent`(100) | 移动 SDK 暴露 `MessageCollector`，可以主动发 click 消息 |
   * | web（本端） | `CustomEvent` `user_click` | **浏览器 SDK 没有对外发 click 消息的 API** —— `MouseClick`(68) 只在它自己的 click observer 内部构造（`app.send(...)` 不导出），所以只能退化成自定义事件 |
   *
   * ## 本端应当保持 0 调用点
   *
   * web 上点击**永远有 DOM 宿主**，而有宿主就该用 `data-openreplay-label`：
   * SDK 的 `attachListener(document,"click")` 无条件发一条 `MouseClick`（关不掉），
   * 属性只是给它**取名字**，零额外记录。此时再调本方法就是把同一次点击抄两遍。
   *
   * 所以本方法只留给「拿不到 DOM 宿主」的场景（桥回调、程序化动作）。
   * 需要带可查询字段时发**有业务含义的事件**，别指望 click：
   *
   * ```tsx
   * <button
   *   data-openreplay-label="brief_card_open"
   *   onClick={() => { Track.event("brief_card_open", { card_id }); … }}
   * />
   * ```
   *
   * ## 带参数：`event` + `params`（与 native 同签名同语义）
   *
   * click 消息在协议层就没有 payload（只有 `label/x/y/selector/hesitationTime`），
   * 所以参数不可能挂在点击本体上。给出业务事件名，本方法内部发那条 `CustomEvent`：
   *
   * ```ts
   * Track.click("brief_card_track");                                   // 无参
   * Track.click("brief_card_track", "brief_card_action", { card_id }); // 带参
   * ```
   *
   * 业务事件**自己起名，别复用 label** —— 复用会与 click 消息同名，后台按名聚合时数字翻倍
   * （`scripts/lint-name-collisions.mjs` 防的正是这个）；收成一个固定名又把
   * 「这是什么事」降级进 payload，浪费 `name` 这个一等字段。
   *
   * 声明式写法用 `data-or-event` + `data-or-param-*`（见 `domBridge.ts`），
   * 那是本端的首选 —— 写属性不写代码。
   */
  click(label: string, event?: string, params?: TrackParams): void {
    try {
      emitOpenReplayEvent(
        "user_click",
        envelope({ label, ...redact(params) }, "click"),
      );
      // 只在给了业务事件名时才发第二条（规范 §5：无参不许纯重复）
      if (event) {
        emit(event, params, "action");
      }
    } catch {
      // 埋点不影响业务
    }
  },

  /**
   * 业务日志。三个级别**一律输出**，不做 level gate / 采样 / 限流（规范 §6）。
   *
   * ## ⚠️ 不进 OpenReplay 事件流（规范 §1.1）
   *
   * 本方法只写 `console`（dev + prod 都写）。它能出现在 OpenReplay 里，**完全依赖**
   * tracker 的 console 采集 —— 落在**console 面板**，不是事件流，且
   * `CONSOLE_CAPTURE_ENABLED` 一关就断（方案 Step 10）。
   *
   * | 需求 | 用什么 |
   * |---|---|
   * | 要能检索 / 聚合 / 做看板 / 报警 | `Track.event` —— **不是本方法** |
   * | 要进 OpenReplay 的 Errors 视图 | `Track.error` |
   * | 排障上下文，可以随 console 采集一起消失 | 本方法 |
   * | 本地临时调试 | 直接 `console.log`，且不该提交 |
   *
   * native 侧对称：`Track.log` → `EdLog`（控制台 + Crashlytics 面包屑），
   * SDK `logs = false` 所以同样不进 OR。
   */
  log(level: TrackLevel, tag: string, msg: string, kv?: TrackParams): void {
    try {
      // ⚠️ **不进 OpenReplay。**（2026-08-10 决策）
      //
      // 日志是排障用的，事件是分析用的 —— 两回事。早期版本把日志也发成一条名为 `log`
      // 的自定义事件，结果 707 个调用点、243 个 tag 的排障信息灌进事件流，
      // 淹没了真正的 122 个业务事件。
      //
      // 要在 OpenReplay 里留痕的只有两类：**业务事件**（`Track.event/view`）与
      // **错误**（`Track.error`，OpenReplay 有专门的 Errors 视图）。
      const safeKv = redact(kv);
      console[level](`[${tag}] ${msg}`, safeKv);
    } catch {
      // 埋点不影响业务
    }
  },

  /** 进入屏幕：设置 scene / screen，影响其后所有事件的 envelope。 */
  screen(scene: string, screenName?: string): void {
    try {
      setRuntimeScreen(scene, screenName);
    } catch {
      // 埋点不影响业务
    }
  },

  /** 登录后写身份。`userId` 同时作为 OpenReplay 的 setUserID。 */
  identify(userId: string, profile?: TrackProfile): void {
    try {
      if (userId) {
        getTracker()?.setUserID?.(userId);
      }
      // 逐事件带的身份（见 `envelope.ts` 的 setEnvelopeIdentity 注释）：
      // 有 ownerId 就是登录态，否则 userId 是匿名 deviceId。
      const ownerId = profile?.ownerId == null ? null : String(profile.ownerId);
      setEnvelopeIdentity(ownerId ? null : userId || null, ownerId);
      if (profile) {
        applyIdentity(profile, setMetadata);
      }
    } catch {
      // 埋点不影响业务
    }
  },

  /**
   * **登出必须调。** metadata 没有 delete，只能整体覆盖成空串，
   * 否则同设备换账号会把上一个用户的身份串到新 session（数据字典 §3.1 规则 2）。
   */
  clearIdentity(): void {
    try {
      setEnvelopeIdentity(null, null);
      clearIdentityFields(setMetadata);
    } catch {
      // 埋点不影响业务
    }
  },

  /** 会话级上下文（数据字典 §3.3 白名单内的 key 才写）。 */
  context(patch: Record<string, string | null | undefined>): void {
    try {
      for (const [key, value] of Object.entries(patch)) {
        if (!value || !ALLOWED_CONTEXT_KEYS.has(key)) {
          continue;
        }
        setMetadata(key, value);
      }
    } catch {
      // 埋点不影响业务
    }
  },

  /** 错误：交给 OpenReplay 的 handleError（会进 session 的 error 时间线）。 */
  error(e: unknown, meta?: Record<string, unknown>): void {
    try {
      getTracker()?.handleError?.(e as Error, {
        ...redact(meta),
        container: container(),
      });
    } catch {
      // 埋点不影响业务
    }
  },
};

export type { TrackParams, TrackProfile };
